//import 'cordova-plugin-inappbrowser';
import logger from 'utils/logger';

import { useGenerateVrPaymentPayPalFastLinkMutation } from '../../../generated/rbi-graphql';
import { getConfigValue, isNative } from '../../../utils/environment';

import { OutcomeResult } from './order-payment/hosted-page-payment/types';
import { useBraintree } from './use-braintree';

async function openNativeModal(url: string): Promise<OutcomeResult> {
  if (!window.cordova) {
    throw new Error('"window.cordova" is not defined');
  }
  if (!(window.cordova as any).InAppBrowser) {
    throw new Error('"window.cordova.InAppBrowser" is not defined');
  }

  const InAppBrowser = (window.cordova as any).InAppBrowser;

  return new Promise(resolve => {
    const win = InAppBrowser.open(url, '_blank', 'location=yes');
    win.addEventListener('message', (e: any) => {
      if (e.data.type !== 'payment-outcome') {
        return;
      }

      win.close();
      resolve(e.data.body as OutcomeResult);
    });
  });
}

function openWebModal(url: string, win: Window | null): Promise<OutcomeResult> {
  if (!win) {
    throw new Error('PayPal window was not created');
  }

  win.location.href = url;
  return new Promise(resolve => {
    function listener(e: MessageEvent) {
      const origin = new URL(e.origin);
      if (!origin.hostname.endsWith('.rbictg.com')) {
        return;
      }
      if (e.data.type !== 'payment-outcome') {
        return;
      }
      win?.close();
      window.removeEventListener('message', listener);
      resolve(e.data.body as OutcomeResult);
    }

    window.addEventListener('message', listener);
  });
}

function getTokenizationKey(): string {
  const vrPaymentConfig = getConfigValue('vrPayment');
  if (!vrPaymentConfig) {
    throw new Error('VR Payment config not found');
  }
  if (!vrPaymentConfig.braintreeTokenizationKey) {
    throw new Error('Braintree tokenization key not found');
  }
  return vrPaymentConfig.braintreeTokenizationKey;
}

export const useVrPaymentPayPal = () => {
  const { getDeviceToken: getBraintreeDeviceToken } = useBraintree();
  const [generateVrPaymentPayPalFastLink] = useGenerateVrPaymentPayPalFastLinkMutation();

  async function openModal(rbiOrderId: string): Promise<OutcomeResult> {
    try {
      const win = isNative ? null : window.open('/loading');
      const deviceToken = await getDeviceToken();

      const { data, errors } = await generateVrPaymentPayPalFastLink({
        variables: {
          input: {
            deviceToken,
            rbiOrderId,
            response: isNative ? 'cordovaiab' : 'opener',
          },
        },
      });

      if (errors) {
        throw errors;
      }

      let outcome: OutcomeResult;
      if (isNative) {
        outcome = await openNativeModal(data!.generateVrPaymentPayPalFastLink.link);
      } else {
        outcome = await openWebModal(data!.generateVrPaymentPayPalFastLink.link, win);
      }

      return outcome;
    } catch (error) {
      logger.error({
        message: 'VR Payment: PayPal integration threw an error',
        error,
      });
      throw error;
    }
  }

  function getDeviceToken() {
    const tokenizationKey = getTokenizationKey();
    return getBraintreeDeviceToken(tokenizationKey);
  }

  return {
    openModal,
    getDeviceToken,
  };
};
